/**
 * Plans Service
 * Handles all plan-related API calls (user trip builder)
 * Plans are user-owned itineraries with plan_days and plan_items
 *
 * Endpoints:
 *   GET    /plans           — List user's plans (search, filter, sort)
 *   GET    /plans/:id       — Plan detail with days & items
 *   POST   /plans           — Create new plan
 *   PUT    /plans/:id       — Update plan (nested days/items)
 *   DELETE /plans/:id       — Delete plan
 *   POST   /plans/:id/duplicate — Duplicate an existing plan
 *   POST   /plans/:id/lock  — Finalize plan (draft → locked)
 *   POST   /plans/:id/unlock — Reopen plan for editing (locked → draft)
 *   POST   /plans/:id/request — Submit a booking enquiry for a locked plan
 *   PATCH  /plans/:id/share  — Toggle a public read-only share link
 *   GET    /plans/shared/:slug — Public read-only shared plan (no auth)
 */

import api from './api';

const plansService = {
  /**
   * Get user's plans with optional search, filter, sort, pagination
   * @param {Object} filters - { search, status, trip_type, sort, page, per_page }
   */
  getPlans: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/plans${queryString}`, { includeAuth: true });
      const payload = response?.data ?? response;
      const plans = payload?.plans ?? payload?.trips ?? [];
      return {
        plans: Array.isArray(plans) ? plans : [],
        pagination: response?.pagination ?? payload?.pagination ?? {},
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get plan by ID with full detail (days, items, cost breakdown)
   */
  getPlanById: async (id) => {
    try {
      const response = await api.get(`/plans/${id}`, { includeAuth: true });
      const plan = response?.data?.plan ?? response?.plan ?? response?.data ?? response;
      return plan && typeof plan === 'object' ? { plan } : { plan: null };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new plan (returns plan + redirect_url to builder)
   */
  createPlan: async (planData) => {
    try {
      const response = await api.post('/plans', { plan: planData }, { includeAuth: true });
      const payload = response?.data ?? response;
      const plan = payload?.plan ?? payload;
      const redirectUrl = payload?.redirect_url ?? (plan?.id ? `/builder/${plan.id}` : null);
      return { plan, redirect_url: redirectUrl };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update plan with nested plan_days_attributes and plan_items_attributes
   * Supports _destroy flag for removing nested records
   */
  updatePlan: async (id, planData) => {
    try {
      const response = await api.put(`/plans/${id}`, { plan: planData }, { includeAuth: true });
      const payload = response?.data ?? response;
      const plan = payload?.plan ?? payload ?? response;
      return plan && typeof plan === 'object' ? { plan } : { plan: null };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete plan permanently
   */
  deletePlan: async (id) => {
    try {
      await api.delete(`/plans/${id}`, { includeAuth: true });
      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Duplicate an existing plan (creates a copy in draft status)
   * Returns new plan + redirect_url
   */
  duplicatePlan: async (id) => {
    try {
      const response = await api.post(`/plans/${id}/duplicate`, null, { includeAuth: true });
      const payload = response?.data ?? response;
      const plan = payload?.plan ?? payload;
      const redirectUrl = payload?.redirect_url ?? (plan?.id ? `/builder/${plan.id}` : null);
      return { plan, redirect_url: redirectUrl };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Lock/finalize a plan (draft → locked)
   * Locked plans are ready for booking
   */
  lockPlan: async (id) => {
    try {
      const response = await api.post(`/plans/${id}/lock`, null, { includeAuth: true });
      const payload = response?.data ?? response;
      return { plan: payload?.plan ?? payload };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Unlock a plan for editing (locked → draft)
   */
  unlockPlan: async (id) => {
    try {
      const response = await api.post(`/plans/${id}/unlock`, null, { includeAuth: true });
      const payload = response?.data ?? response;
      return { plan: payload?.plan ?? payload };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get the current user's own trip requests (customer view).
   * @param {Object} filters - { status, page, per_page }
   */
  getMyRequests: async (filters = {}) => {
    try {
      const queryString = api.buildQueryString(filters);
      const response = await api.get(`/plan_requests${queryString}`, { includeAuth: true });
      const data = response?.data ?? response;
      return {
        requests: data?.plan_requests ?? [],
        pagination: response?.pagination ?? data?.pagination ?? {},
      };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Submit a booking enquiry for a finalized (locked) plan.
   * @param {string} id - Plan ID
   * @param {Object} details - { num_travelers, preferred_date, message }
   * Returns the created plan_request.
   */
  requestPlan: async (id, details = {}) => {
    try {
      const response = await api.post(`/plans/${id}/request`, { plan_request: details }, { includeAuth: true });
      const payload = response?.data ?? response;
      return { planRequest: payload?.plan_request ?? payload, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Publish a plan as a marketplace trip (admin only).
   * @param {string} id - Plan ID
   * @param {string} visibility - 'public' | 'draft' | 'hidden'
   */
  publishPlan: async (id, visibility = 'public') => {
    try {
      const response = await api.post(`/plans/${id}/publish`, { visibility }, { includeAuth: true });
      const payload = response?.data ?? response;
      return { trip: payload?.trip ?? payload, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Book an approved plan (creates a booking against a private trip).
   * @param {string} id - Plan ID
   * @param {Object} details - { travel_date, num_travelers, customer_phone }
   */
  bookPlan: async (id, details = {}) => {
    try {
      const response = await api.post(`/plans/${id}/book`, details, { includeAuth: true });
      const payload = response?.data ?? response;
      return { booking: payload?.booking ?? payload, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Toggle a plan's public read-only share link (owner only).
   * Returns the plan's { id, slug, is_shared }.
   */
  sharePlan: async (id) => {
    try {
      const response = await api.patch(`/plans/${id}/share`, null, { includeAuth: true });
      const payload = response?.data ?? response;
      return { plan: payload?.plan ?? payload, message: response?.message };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Fetch a public shared plan by slug (no auth). 404s when not shared.
   */
  getSharedPlan: async (slug) => {
    try {
      const response = await api.get(`/plans/shared/${slug}`, { includeAuth: false });
      const plan = response?.data?.plan ?? response?.plan ?? response?.data ?? response;
      return plan && typeof plan === 'object' ? { plan } : { plan: null };
    } catch (error) {
      throw error;
    }
  },

  /**
   * Guest enquiry against a shared plan (no auth). Files a PlanRequest that shows
   * up in the admin Trip Requests view flagged as a guest.
   * @param {string} slug
   * @param {Object} details - { guest_name, guest_email, guest_phone, num_travelers, message }
   */
  requestSharedPlan: async (slug, details = {}) => {
    try {
      const response = await api.post(`/plans/shared/${slug}/request`, { plan_request: details }, { includeAuth: false });
      const payload = response?.data ?? response;
      return { planRequest: payload?.plan_request ?? payload, message: response?.message };
    } catch (error) {
      throw error;
    }
  },
};

export default plansService;
